import React, { useEffect, useMemo, useState } from "react";
import "./CookieBanner.css";
import { useMetaPixel } from "../../hooks/useMetaPixel";

type ThemeMode = "dark" | "light";

const CookieBanner: React.FC = () => {
  const { acceptConsent, declineConsent } = useMetaPixel();
  const [isVisible, setIsVisible] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>("dark");

  useEffect(() => {
    if (!localStorage.getItem("cookie_consent")) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    const readTheme = (): ThemeMode => {
      const storedTheme = localStorage.getItem("theme");
      if (storedTheme === "light" || storedTheme === "dark") {
        return storedTheme;
      }

      if (document.documentElement.classList.contains("light")) {
        return "light";
      }

      return "dark";
    };

    setTheme(readTheme());

    const handleStorage = (event: StorageEvent) => {
      if (event.key === "theme") {
        if (event.newValue === "light" || event.newValue === "dark") {
          setTheme(event.newValue);
        }
      }
    };

    const observer = new MutationObserver(() => {
      if (document.documentElement.classList.contains("dark")) {
        setTheme("dark");
        return;
      }

      if (document.documentElement.classList.contains("light")) {
        setTheme("light");
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
      observer.disconnect();
    };
  }, []);

  const themeClassName = useMemo(() => {
    return theme === "light" ? "cookie-banner--light" : "cookie-banner--dark";
  }, [theme]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`cookie-banner ${themeClassName}`}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="cookie-banner__content">
        <p>
          We use cookies to improve your experience and measure site usage.
        </p>
        <div className="cookie-banner__actions">
          <button
            className="cookie-banner__button cookie-banner__button--secondary"
            onClick={() => {
              declineConsent();
              setIsVisible(false);
            }}
            type="button"
          >
            Decline
          </button>
          <button
            className="cookie-banner__button cookie-banner__button--primary"
            onClick={() => {
              acceptConsent();
              setIsVisible(false);
            }}
            type="button"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
